import { AbsoluteFill } from "remotion";
import { Caption, COLORS, FONTS, MetricCounter, SlideTimeBar, type ScriptLine } from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, LEAK, STOP, ROOM, CRAG, MEASURE, OUTRO } from "./script";
import { Answer, Banner, BottomBar, Caret, ChatHeader, Chip, Diff, QualityCard, Reveal, Step, Win } from "./ui";

// ── scene frame ──────────────────────────────────────────────────────────────
const Frame: React.FC<{ caption: ScriptLine[]; accent: "cyan" | "green" | "amber" | "violet" | "red"; dur: number; children: React.ReactNode }> = ({
  caption,
  accent,
  dur,
  children,
}) => (
  <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", padding: "64px 60px 150px" }}>
    {children}
    <Caption lines={caption} accent={accent} />
    <SlideTimeBar sceneDurationFrames={dur} />
  </AbsoluteFill>
);

const hl = (c: string): React.CSSProperties => ({ color: c, fontWeight: 600 });
const body: React.CSSProperties = { padding: 24, display: "flex", flexDirection: "column", gap: 12 };
const cites: React.CSSProperties = { fontFamily: FONTS.mono, fontSize: 16, color: COLORS.dim, marginTop: 2 };

const Q = "How is the EAKC pipeline implemented?";

// ── Scene 01 — Hook (the real chat UI) ───────────────────────────────────────
export const Scene01Hook: React.FC = () => (
  <Frame caption={HOOK} accent="cyan" dur={SCENE.hook.duration}>
    <Win>
      <ChatHeader question={Q} />
      <div style={body}>
        <Reveal at={8}>
          <div
            style={{
              alignSelf: "flex-end",
              maxWidth: 880,
              background: COLORS.panel2,
              border: `1px solid ${COLORS.line}`,
              borderRadius: 14,
              padding: "14px 20px",
              fontFamily: FONTS.body,
              fontSize: 20,
              color: COLORS.text,
            }}
          >
            Explain how the pipeline in EAKC got implemented — step by step.
          </div>
        </Reveal>
        <Step at={34} name="[Query] Body Parsed" status="complete" time="pipeline = multi-agent" />
        <Step at={56} name="Source-Aware Decomposition" status="complete" time="4 sub-queries" accent={COLORS.cyan} detail="all pinned to source: EAKC · intent code_flow" />
        <Step at={84} name="Whole-System Synthesis" status="in_progress" accent={COLORS.amber} detail="architecture grounding…" />
      </div>
      <BottomBar input="Explain how the pipeline in EAKC got implemented…" />
    </Win>
  </Frame>
);

// ── Scene 02 — Wrong project (cross-source leak) ─────────────────────────────
export const Scene02Leak: React.FC = () => (
  <Frame caption={LEAK} accent="red" dur={SCENE.leak.duration}>
    <Win>
      <ChatHeader question={Q} />
      <div style={body}>
        <Answer tone="bad" title="The EAKC pipeline">
          Pipeline Agents handle <span style={hl(COLORS.red)}>brokers</span>, <span style={hl(COLORS.red)}>orders</span> and{" "}
          <span style={hl(COLORS.red)}>portfolios</span>; the <span style={hl(COLORS.red)}>OMS</span> routes trades, and{" "}
          <span style={hl(COLORS.red)}>SQLAlchemy</span> models persist positions…
        </Answer>
        <div style={cites}>
          cited sources: <span style={hl(COLORS.green)}>retrieval.ts ✓ · base-agent.ts ✓ · synthesis-agent.ts ✓</span> — all EAKC
        </div>
        <Reveal at={70}>
          <Banner tone="bad">citations: 8 × EAKC ✓ &nbsp;·&nbsp; prose: a different project (a trading system) ✗</Banner>
        </Reveal>
        <Reveal at={180}>
          <Step name="Turn Hard-Scope Final Filter" status="complete" accent={COLORS.green} detail="dropped 12 chunks from outside the asked-for project — before synthesis" />
        </Reveal>
        <Reveal at={300}>
          <Banner tone="good">fix · lock every answer to the detected source, drop other-project chunks at the chokepoint</Banner>
        </Reveal>
      </div>
    </Win>
  </Frame>
);

// ── Scene 03 — Wrong length (the "The E" stop) ───────────────────────────────
export const Scene03Stop: React.FC = () => (
  <Frame caption={STOP} accent="amber" dur={SCENE.stop.duration}>
    <Win w={1240}>
      <ChatHeader question="…explain each pipeline step in detail" />
      <div style={body}>
        <QualityCard
          rows={[
            { label: "Response generated: 2 words", ok: false },
            { label: "Citations embedded: 8 source references", ok: true },
            { label: <span>Token efficiency: <b>2 completion tokens for 2 words</b></span>, ok: false },
          ]}
          note="Pipeline mode: Standard (single-pass) · the model thought silently"
        />
        <Answer tone="bad">
          <span style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.text }}>The E</span>
          <Caret color={COLORS.red} />
        </Answer>
        <Reveal at={120}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "12px 10px" }}>
            <Diff sign="-">provider: {"{ sort: 'throughput' }"}</Diff>
            <Diff sign=" ">reasoning: {"{ enabled: false }"}&nbsp;&nbsp;// now actually honored</Diff>
          </div>
        </Reveal>
        <Reveal at={250}>
          <Banner tone="good">fix · reverted the routing — full answers stream again</Banner>
        </Reveal>
      </div>
    </Win>
  </Frame>
);

// ── Scene 04 — Wrong room (the admin tour) ───────────────────────────────────
export const Scene04Room: React.FC = () => (
  <Frame caption={ROOM} accent="violet" dur={SCENE.room.duration}>
    <Win>
      <ChatHeader question="…what part of the pipeline runs for each query type" />
      <div style={body}>
        <Step name="Whole-System Synthesis" status="complete" accent={COLORS.amber} detail="codebase map = file_index ranked by PageRank (popularity)" />
        <Answer tone="bad" title="## Major Subsystems">
          <span style={hl(COLORS.violet)}>src/app/admin/diagnostics</span> · admin/jobs · admin/tenants · admin/users · admin/webhooks · digest…
        </Answer>
        <Reveal at={120}>
          <Banner tone="bad">ranked by popularity → the most-imported admin pages win, the pipeline loses</Banner>
        </Reveal>
        <Reveal at={300}>
          <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "12px 10px" }}>
            <Diff sign="+">rank the File Index by relevance to the query (retrieved-dir + query-term), not PageRank</Diff>
          </div>
        </Reveal>
        <Reveal at={420}>
          <Answer tone="good" title="✓ now centers on the real pipeline">
            src/lib/rag/retrieval.ts → hybrid search → CRAG → rerank → synthesis
          </Answer>
        </Reveal>
      </div>
    </Win>
  </Frame>
);

// ── Scene 05 — Knew but shipped (CRAG safety net) — LIVE-VERIFIED ─────────────
export const Scene05Crag: React.FC = () => (
  <Frame caption={CRAG} accent="green" dur={SCENE.crag.duration}>
    <Win>
      <ChatHeader question="…with examples for each query type" />
      <div style={body}>
        <Step name="CRAG: Relevance Grader" status="incorrect" accent={COLORS.red} detail="verdict = INCORRECT · confidence 0.90 — the retrieval doesn't answer the question" />
        <Reveal at={90}>
          <Step name="CRAG: HyDE Fallback  (before)" status="skipped" accent={COLORS.dim} detail="decomposed → escalation was a no-op → synthesised anyway" />
        </Reveal>
        <Reveal at={210}>
          <Step
            name="CRAG: Broader Search  (after)"
            status="complete"
            accent={COLORS.green}
            detail="INCORRECT + decomposed → cheaper broader pass instead of the ~50s HyDE · re-graded AMBIGUOUS"
          />
        </Reveal>
        <Reveal at={350}>
          <Banner tone="good">live-verified in prod · session b9877e17 — the fact-checker now retries instead of shipping a known-bad answer</Banner>
        </Reveal>
      </div>
    </Win>
  </Frame>
);

// ── Scene 06 — Measure the residual miss ─────────────────────────────────────
const evalRow: React.CSSProperties = { fontFamily: FONTS.mono, fontSize: 18, color: COLORS.text, lineHeight: 2 };

export const Scene06Measure: React.FC = () => (
  <Frame caption={MEASURE} accent="cyan" dur={SCENE.measure.duration}>
    <Win w={1320}>
      <div style={{ padding: "18px 24px", borderBottom: `1px solid ${COLORS.line}`, background: COLORS.panel, fontFamily: FONTS.mono, fontSize: 18, color: COLORS.text }}>
        /admin/eval &nbsp;·&nbsp; <span style={hl(COLORS.cyan)}>golden cases</span>
      </div>
      <div style={{ padding: 26 }}>
        <Reveal at={10}>
          <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.cyan}44`, borderLeft: `3px solid ${COLORS.cyan}`, borderRadius: 10, padding: "18px 22px" }}>
            <div style={evalRow}>
              <span style={hl(COLORS.cyan)}>code-flow-002-pipeline-impl</span>
            </div>
            <div style={evalRow}>
              expect → <span style={hl(COLORS.green)}>src/lib/rag/retrieval.ts</span> surfaces
            </div>
            <div style={evalRow}>
              forbid → <span style={hl(COLORS.red)}>src/app/admin/*</span>
            </div>
            <div style={evalRow}>
              status → <span style={hl(COLORS.amber)}>EXPECTED TO FAIL — tracked, not guessed</span>
            </div>
          </div>
        </Reveal>
        <Reveal at={120}>
          <div style={{ marginTop: 16 }}>
            <Banner tone="warn">the live trace proves it · admin/diagnostics held 9 of 24 slots · retrieval.ts still absent</Banner>
          </div>
        </Reveal>
      </div>
    </Win>
  </Frame>
);

// ── Scene 07 — Outro ─────────────────────────────────────────────────────────
const check: React.CSSProperties = { display: "flex", gap: 12, fontFamily: FONTS.mono, fontSize: 22, color: COLORS.text, marginBottom: 12 };

export const Scene07Outro: React.FC = () => (
  <Frame caption={OUTRO} accent="cyan" dur={SCENE.outro.duration}>
    <Win w={1180}>
      <div style={{ padding: "40px 48px" }}>
        <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.text, marginBottom: 28 }}>
          4 bugs · 4 fixes
        </div>
        <Reveal at={20}>
          <div style={check}><span style={{ color: COLORS.green }}>✓</span> wrong project — turn-scope hard filter</div>
        </Reveal>
        <Reveal at={36}>
          <div style={check}><span style={{ color: COLORS.green }}>✓</span> wrong length — reverted throughput routing</div>
        </Reveal>
        <Reveal at={52}>
          <div style={check}><span style={{ color: COLORS.green }}>✓</span> wrong room — File-Index relevance bias</div>
        </Reveal>
        <Reveal at={68}>
          <div style={check}>
            <span style={{ color: COLORS.green }}>✓</span> knew &amp; shipped — CRAG broader-search safety net{" "}
            <Chip>live</Chip>
          </div>
        </Reveal>
        <Reveal at={110}>
          <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS.green, marginTop: 30 }}>
            Lock the scope. Measure the rest.
          </div>
        </Reveal>
      </div>
    </Win>
    <div style={{ position: "absolute", bottom: 154, right: 90 }}>
      <MetricCounter value={4} suffix=" / 4 fixed" durationFrames={44} accent="green" fontSize={56} />
    </div>
  </Frame>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
